import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { mobxSet } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import * as Graphic from '@src/slide/infrastructure/module/independent/graphic';
import * as Lines
    from '@src/slide/infrastructure/module/dependent/line-info';
import { transaction, observable } from 'mobx';
import { generateConnectorPoints, getConnectorStyleByPoints } from '@src/slide/infrastructure/module/independent/graphic/connector';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import ElementBaseModel from './base-modeles/element-base-model';
import connectorPointOfIndex
    from '@src/slide/domain/ui-model/element-modeles/property/graphic-info/connector-point';
import has from 'lodash/has';
import set from 'lodash/set';
import {
    IBaseModel,
    IConvertSdkToUiDataOptions,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import {
    IConnectorModel,
    IAdsorbInfo,
    IConnectorPointerInfo,
    IPointerInfo,
    IPointerOffsetInfo,
    IExtendPointerInfo,
    IUpdateStyle,
    ILeftAndTop,
} from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { IConnectorProps, IConnectorInfo } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/connector-model-props.interface';
import {
    IGraphicProperty,
    IOperatingGraphicInfo,
} from '@src/slide/domain/ui-model/element-modeles/property/graphic-info/graphic-info.interface';
import { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';
import { IFrame, IMultiModelFrameMap } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { GraphicProperty } from '@src/slide/domain/ui-model/element-modeles/property/graphic-info';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { enumerateGroup } from './property/group-operation/enumerate';
import { LineType, lineTypeByCornerNum } from '@src/slide/infrastructure/module/dependent/line-info';
import { ConnectorCaculater, UpdateSytle } from './property/connector';
import { ModelMenuRefreshEventsKeys } from '@src/slide/domain/ui-model/element-modeles/Interface/model-menu-refresh.interface';
import { IConnectorDataModel } from '@tencent/slide-sdk-model';
import ModelConverterBridge from '@src/slide/domain/ui-model/element-modeles/convert/model-converter-bridge';

const pointIndex = {
    start: 0,
    end: 1,
};

class ConnectorModel extends ElementBaseModel implements IConnectorModel {
    /**
     * 将sdkModel转换为uiModel
     * @param sdkData 合并了继承属性的sdkModel
     * @param uiData 当前uiModel
     * @param rawSdkData 原始sdkModel
     * @param option 附加参数，sdk的observers回调为源头
     */
    public static async convertDataSdkToUi(
        sdkData: IConnectorDataModel,
        uiData: IConnectorModel,
        rawSdkData: IConnectorDataModel,
        option?: IConvertSdkToUiDataOptions,
    ): Promise<void> {
        super.convertDataSdkToUi(sdkData, uiData, rawSdkData, option);

        const convertPromises: Array<Promise<void> | IBaseModel> = [];
        const convertConnectorTask = ModelConverterBridge.convertSdkToUi({
            type: UIModelType.CONNECTOR,
            sdkData,
            uiData,
        });
        convertPromises.push(convertConnectorTask);

        if (uiData.parent?.asyncConvertChildren) {
            await Promise.all(convertPromises);
        }
    }
    /**
     * sdk转ui后的操作
     * @param sdkData 合并了继承属性的sdkModel
     * @param uiData 当前uiModel
     * @param rawSdkData 原始sdkModel
     * @param optionObject 附加参数，sdk的observers回调为源头
     */
    public static didConvertDataSdkToUi(
        sdkData: IConnectorDataModel,
        uiData: ConnectorModel,
        rawSdkData: IConnectorDataModel,
        optionObject: IConvertSdkToUiDataOptions,
    ): void {
        super.didConvertDataSdkToUi(sdkData, uiData, rawSdkData, optionObject);
        uiData.getMenuManager()?.event.emit(ModelMenuRefreshEventsKeys.REFRESH_MENU_ITEM_VALUES);
        uiData.getMenuManager()?.event.emit(ModelMenuRefreshEventsKeys.REFRESH_GROUP_ITEM_VALUES);
    }

    /**
     * 将ui数据转换为sdk类型
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    public static convertDataUiToSdk(uiData: any, sdkData: any, rawUiData: any): void {
        ModelConverterBridge.convertUiToSdk({ type: UIModelType.CONNECTOR, uiData, sdkData, rawUiData });
    }

    declare public sdkData: IConnectorDataModel | null;
    public props: IConnectorProps;
    /**
     * 临时保存吸附点信息
     */
    public adsorbInfo?: IAdsorbInfo;
    /**
     * 组合有旋转时连接线的旋转角度
     */
    public rotateInGroup: number|undefined;
    /**
     * 连接线的样式计算器
     */
    public connectorCaculater: ConnectorCaculater;
    /**
     * 关键点操作临时数据
     */
    @observable public operatingGraphicInfo: IOperatingGraphicInfo | null = null;
    /**
     * 图形信息
     * @private
     */
    private graphicProperty: IGraphicProperty;

    /**
     * 连接线头部连接的图形
     */
    public get startUIModel(): IElementBaseModel | null {
        if (this.props?.style?.connectorInfo?.start?.id
            && has(this, 'props.style.connectorInfo.start.index')
            && this.soruceRefForUI) {
            return this.soruceRefForUI.childUiModelFromId(this.props.style.connectorInfo.start.id);
        }
        return null;
    }

    /**
     * 连接线尾部连接的图形
     */
    public get endUIModel(): IElementBaseModel | null {
        if (this.props?.style?.connectorInfo?.end?.id
            && has(this, 'props.style.connectorInfo.end.index')
            && this.soruceRefForUI) {
            return this.soruceRefForUI.childUiModelFromId(this.props.style.connectorInfo.end.id);
        }
        return null;
    }

    public constructor() {
        super();
        this.type = UIModelType.CONNECTOR;
        this.props = {
            style: {
                left: 0,
                top: 0,
                width: ptToCanvas(281),
                height: ptToCanvas(200),
                opacity: 1,
                position: 'absolute',
                bgLine: null,
                connectorInfo: {
                    start: {},
                    end: {},
                },
            },
        };
        this.connectorCaculater = new ConnectorCaculater(this);
    }

    /**
     * 获取当前model的连接点信息
     */
    public getConnectorInfo(): IConnectorInfo | null {
        const model = this.props.style.connectorInfo;
        if ((model?.start?.id && has(model, 'start.index')) || (model?.end?.id && has(model, 'end.index'))) {
            return this.props.style.connectorInfo;
        }
        return null;
    }

    /**
     * 给当前连接线建立关联
     */
    public buildRelation(): void {
        if (this.props?.style?.connectorInfo?.start?.id
            && has(this, 'props.style.connectorInfo.start.index')) {
            const { id } = this.props.style.connectorInfo.start;
            if (id !== this.id) {
                this.pushToConnectorRefList(id);
            }
        }
        if (this.props?.style?.connectorInfo?.end?.id
            && has(this, 'props.style.connectorInfo.end.index')) {
            const { id } = this.props.style.connectorInfo.end;
            if (id !== this.id) {
                this.pushToConnectorRefList(id);
            }
        }
    }

    /**
     * 获取connector的起始点坐标，相对于Slide
     * @param frame 元素的frame信息
     * @param startEndOffset 端点相对于屏幕的偏移量
     * @param toUpdateModelFrameMap 存储所有选中的图形待更新的frame
     */
    public getStartEndPoints(
        frame: IFrame,
        startEndOffset?: IPointerOffsetInfo,
        toUpdateModelFrameMap?: IMultiModelFrameMap,
    ): IConnectorPointerInfo {
        const { p1, p2 } = this.getPoint(frame, startEndOffset);
        let startP = {
            x: p1.x,
            y: p1.y,
        };
        let endP = {
            x: p2.x,
            y: p2.y,
        };
        if (this.connectedNum() !== 0) {
            if (this.startUIModel) {
                //  连接线的起始位置，如果有绑定的element，就是element的对应index的当前位置
                const { index } = this.props.style.connectorInfo.start!;
                const { startUIModel } = this;
                startP = connectorPointOfIndex(startUIModel, index!, toUpdateModelFrameMap);
            } else {
                this.updatePointInGroup(startP);
            }
            if (this.endUIModel) {
                //  连接线的终止位置，如果有绑定的element，就是element的对应index的当前位置
                const { index } = this.props.style.connectorInfo.end!;
                const { endUIModel } = this;
                endP = connectorPointOfIndex(endUIModel, index!, toUpdateModelFrameMap);
            } else {
                this.updatePointInGroup(endP);
            }
        } else {
            this.updatePointInGroup(startP);
            this.updatePointInGroup(endP);
        }
        // 吸附
        if (this.adsorbInfo?.adsorbPoint) {
            if (this.adsorbInfo.endIndex === pointIndex.start) {
                startP = this.adsorbInfo.adsorbPoint;
            } else if (this.adsorbInfo.endIndex === pointIndex.end) {
                endP = this.adsorbInfo.adsorbPoint;
            }
        }
        return { startP, endP };
    }

    public updateModelStyle(updateStlye: UpdateSytle): void {
        transaction(() => {
            if (updateStlye.bgLine) {
                set(this.props.style, 'bgLine', updateStlye.bgLine);
            }
            if (updateStlye.controlValues) {
                set(this.props.style, 'controlValues', updateStlye.controlValues);
            }
            mobxSet(this, 'operationFrame', updateStlye.operationFrame);
        });
    }

    /**
     * 更新连接线类型
     * @param startP 头信息
     * @param endP 尾信息
     * @param lineType 线类型
     */
    public changeLooks(startP: IPointerInfo, endP: IPointerInfo, lineType: ShapeType): void {
        const { frame, bgLine, controlValues } = this.getConnectedValidateLineTypeStyle(startP, endP, lineType);
        transaction(() => {
            set(this.props.style, 'controlValues', controlValues);
            set(this.props.style, 'bgLine', bgLine);
            mobxSet(this, 'operationFrame', frame);
        });
    }

    /**
     * 生成连接线绘制路径上的所有点集
     * @param connectorModel 连接线uiModel
     * @param isOperateIng 是否操作中
     * @param startPos 头信息
     * @param endPos 尾信息
     * @param toUpdateModelFrameMap 存储所有元素待更新样式
     */
    public getPoints(
        connectorModel: IConnectorModel,
        isOperateIng: boolean,
        startPos: IPointerInfo,
        endPos: IPointerInfo,
        toUpdateModelFrameMap?: IMultiModelFrameMap,
    ): IPointerInfo[] {
        const lineType = connectorModel.props.style.bgLine || Lines.LineType.STRAIGNTCONNECTOR1;
        let points: Array<IPointerInfo> = [];
        if (!lineType.startsWith('straightConnector')) {
            const endInfo = this.getConnectorEndInfo(
                connectorModel,
                startPos, endPos, toUpdateModelFrameMap, isOperateIng,
            );
            points = generateConnectorPoints(endInfo.start, endInfo.end);
        } else {
            points = [{ ...startPos }, { ...endPos }];
        }
        points = points.map(point => this.getFrame().pointToElement(point, isOperateIng));
        return points;
    }

    /**
     * 获取要删除的关联图形信息
     */
    // eslint-disable-next-line complexity
    public toRemoveConnectorInfoProps(): { style?: { connectorInfo: IConnectorInfo } } | undefined {
        let toRemoveProps;
        if (
            this.sdkData?.props?.nvPr?.stCxn?.id
            && (this.sdkData?.props?.nvPr?.stCxn?.idx || this.sdkData?.props?.nvPr?.stCxn?.idx === 0)
        ) {
            if (!(has(this.props.style, 'connectorInfo.start.id') && has(this.props.style, 'connectorInfo.start.index'))) {
                toRemoveProps = {};
                set(toRemoveProps, 'style.connectorInfo.start', {
                    id: this.sdkData?.props?.nvPr?.stCxn?.id,
                    index: this.sdkData?.props?.nvPr?.stCxn?.idx,
                });
            }
        }
        if (
            this.sdkData?.props?.nvPr?.endCxn?.id
            && (this.sdkData?.props?.nvPr?.endCxn?.idx || this.sdkData?.props?.nvPr?.endCxn?.idx === 0)
        ) {
            if (!(has(this.props.style, 'connectorInfo.end.id') && has(this.props.style, 'connectorInfo.end.index'))) {
                if (!toRemoveProps) toRemoveProps = {};
                set(toRemoveProps, 'style.connectorInfo.end', {
                    id: this.sdkData?.props?.nvPr?.endCxn?.id,
                    index: this.sdkData?.props?.nvPr?.endCxn?.idx,
                });
            }
        }
        return toRemoveProps;
    }

    /**
     * 获取当连接线连接的元素更新时，连接线更新的style信息。此接口提供于直接修改连接元素大小
     * @param toUpdateModelFrameMap 存储所有元素待更新样式
     */
    public getUpdateStyleProps = (toUpdateModelFrameMap: IMultiModelFrameMap): IUpdateStyle => {
        const { startP, endP } = this.getStartEndPoints(
            this.getFrame().getFrameInfo(false),
            undefined, toUpdateModelFrameMap,
        );
        const lineType = this.props.style.bgLine || Lines.LineType.STRAIGNTCONNECTOR1;
        return this.getStyleProps(startP, endP, lineType, toUpdateModelFrameMap);
    };

    // 如果只有一端连接了元素，并且元素正在移动，则连接线也跟随移动
    public getUpdateStylePropsForOnlyMove = ({ left, top }: ILeftAndTop): IFrame => {
        const { width, height, flipH, flipV, rotate } = this.props.style;
        return ({
            left: this.props.style.left + left,
            top: this.props.style.top + top,
            width,
            height,
            flipH: flipH ? 1 : 0,
            flipV: flipV ? 1 : 0,
            rotate: rotate ? rotate : 0,
        });
    };

    /**
     * 图形信息接口
     */
    public getGraphic(): IGraphicProperty {
        if (!this.graphicProperty) {
            this.graphicProperty = new GraphicProperty(this);
        }
        return this.graphicProperty;
    }

    public resetStartAndEndForMove(isOnlyMove?: boolean): void {
        if (!isOnlyMove) return;
        const { startUIModel, endUIModel } = this;
        // 如果连接的图形也没有被选中，需要被置空
        if (startUIModel && !startUIModel.selected) {
            this.props.style.connectorInfo.start = null;
            startUIModel.removeRelatedUIModelRefs(this);
        }
        if (endUIModel && !endUIModel.selected) {
            this.props.style.connectorInfo.end = null;
            endUIModel.removeRelatedUIModelRefs(this);
        }
    }

    public clearStartAndEnd(): void {
        const { startUIModel, endUIModel } = this;
        if (startUIModel) {
            this.props.style.connectorInfo.start = null;
            startUIModel.removeRelatedUIModelRefs(this);
        }
        if (endUIModel) {
            this.props.style.connectorInfo.end = null;
            endUIModel.removeRelatedUIModelRefs(this);
        }
    }

    public canSetArrowStyle(): boolean {
        return !!this.props.style.bgLine;
    }

    public updateLook(): void {
        const { startP, endP } = this.getStartEndPoints(this.getFrame().getFrameInfo(!!this.operationFrame));
        const points = this.getPoints(this, true, startP, endP);
        const style = getConnectorStyleByPoints(points);
        const lineType = this.props.style.bgLine || LineType.STRAIGNTCONNECTOR1;
        if (points) {
            const newLineType: string = lineTypeByCornerNum(points.length, lineType) as string;
            if (newLineType) {
                this.props.style.bgLine = newLineType;
            }
        }
        this.props.style.controlValues = style.adjs;
        this.props.style.rotate = style.rot;
        this.props.style.flipH = style.flipH ? 1 : 0;
        this.props.style.flipV = style.flipV ? 1 : 0;
        this.props.style.left = style.left;
        this.props.style.top = style.top;
        this.props.style.width = style.width;
        this.props.style.height = style.height;
    }
    /**
     * 获取 肘形/曲线连接线 并且 有连接元素的style
     */
    public getConnectedValidateLineTypeStyle = (
        startP: IPointerInfo, endP: IPointerInfo, lineType: ShapeType,
        toUpdateModelFrameMap?: IMultiModelFrameMap,
    ): IUpdateStyle => {
        const points = toUpdateModelFrameMap
            ? this.getPoints(this, false, startP, endP, toUpdateModelFrameMap)
            : this.getPoints(this, true, startP, endP);
        const style = getConnectorStyleByPoints(points);
        return {
            frame: {
                rotate: style.rot,
                flipH: style.flipH ? 1 : 0,
                flipV: style.flipV ? 1 : 0,
                left: style.left,
                top: style.top,
                width: style.width,
                height: style.height,
            },
            bgLine: Lines.lineTypeByCornerNum(points.length, lineType),
            controlValues: style.adjs,
        };
    };

    /**
     * 获取连接线关联的图形数量
     * @param connectorModel uiModel
     * @private 数量
     */
    private connectedNum(): number {
        let num = 0;
        if (this.startUIModel) {
            num += 1;
        }
        if (this.endUIModel) {
            num += 1;
        }
        return num;
    }

    /**
     * 将图形sdkId加入当前连接线关联列表
     * @param id 图形sdkId
     */
    private pushToConnectorRefList(id: string): void {
        if (!id || !this.soruceRefForUI) return;

        const model = this.soruceRefForUI.childUiModelFromId(id);
        if (!(model?.relatedUIModelRefs)) return;

        const connectorRefList = model.relatedUIModelRefs;

        const res = connectorRefList.find(ref => ref === this);

        if (res) return;

        connectorRefList.push(this);
    }

    /**
     * 根据元素的frame信息和偏移量得出新的端点信息
     * @param frame 元素的frame信息
     * @param startEndOffset 端点相对于屏幕的偏移量
     * @private
     */
    private getPoint(frame: IFrame, startEndOffset?: IPointerOffsetInfo) {
        const rect = {
            left: frame.left,
            top: frame.top,
            right: frame.left + frame.width,
            bottom: frame.top + frame.height,
        };
        const center = {
            x: ((2 * frame.left) + frame.width) / 2,
            y: ((2 * frame.top) + frame.height) / 2,
        };
        let p1 = {
            x: rect.left,
            y: rect.top,
        };
        let p2 = {
            x: rect.right,
            y: rect.bottom,
        };
        ({ p1, p2 } = this.adjustPointWithFlipRotate(frame, p1, center, p2));
        if (!startEndOffset) return { p1, p2 };
        // offset是点移动的绝对偏移量，这里需要将点转成相对于slide上的坐标后再加offset
        this.updatePointInGroup(p1);
        this.updatePointInGroup(p2);
        if (startEndOffset.endIndex === 0) {
            p1 = {
                x: p1.x + startEndOffset.offset.x,
                y: p1.y + startEndOffset.offset.y,
            };
        } else if (startEndOffset.endIndex === 1) {
            p2 = {
                x: p2.x + startEndOffset.offset.x,
                y: p2.y + startEndOffset.offset.y,
            };
        }
        // 加完offset之后要转回相对于父元素的坐标
        this.connectorCaculater.updatePointInGroup(p1);
        this.connectorCaculater.updatePointInGroup(p2);
        return { p1, p2 };
    }

    /**
     * 端点的计算加入翻转和旋转因素
     * @param frame 元素的frame信息
     * @param p1 端点1
     * @param center 元素中心点
     * @param p2 端点2
     * @private
     */
    private adjustPointWithFlipRotate(frame: IFrame, p1: IPoint, center: IPoint, p2: IPoint) {
        if (frame?.flipH === 1) {
            p1 = Graphic.afterFlipH(p1, center.x);
            p2 = Graphic.afterFlipH(p2, center.x);
        }
        if (frame?.flipV === 1) {
            p1 = Graphic.afterFlipV(p1, center.y);
            p2 = Graphic.afterFlipV(p2, center.y);
        }
        if (frame?.rotate) {
            const { rotate } = frame;
            p1 = Graphic.afterRotate(p1, rotate, center.x, center.y);
            p2 = Graphic.afterRotate(p2, rotate, center.x, center.y);
        }
        return { p1, p2 };
    }

    /**
     * 端点的计算加入组合因素，此处获取的是端点相对于slide左上角的绝对坐标，入参是相对于父元素的相对坐标
     * @param point 端点
     * @private
     */
    private updatePointInGroup(point: IPoint): void {
        const isGroupChildren = !!(this.parent && this.parent.type === UIModelType.GROUP);

        if (isGroupChildren) {
            const newPoint = this.getFrame().pointOnSlide(point, false);
            point.x = newPoint.x;
            point.y = newPoint.y;
        }
    }

    /**
     * 是否为肘形/曲线连接线
     * @param lineType
     * @private
     */
    private validateLineType(lineType: ShapeType): boolean {
        return ((expect(lineType).isString() && lineType.startsWith('bentConnector'))
            || (expect(lineType).isString() && lineType.startsWith('curvedConnector')));
    }

    /**
     * 构造出连接线两端的详细数据
     * @param connectorModel
     * @param startPos
     * @param endPos
     * @param toUpdateModelFrameMap 存储所有选中的图形待更新的frame
     * @private
     */
    private getConnectorEndInfo(
        connectorModel: IConnectorModel, startPos: IPointerInfo, endPos: IPointerInfo,
        toUpdateModelFrameMap?: IMultiModelFrameMap, isOperateIng?: boolean,
    ) {
        const start: IExtendPointerInfo = {
            x: Math.round(startPos.x), y: Math.round(startPos.y), direction: startPos.direction,
        };
        const end: IExtendPointerInfo = {
            x: Math.round(endPos.x), y: Math.round(endPos.y), direction: endPos.direction,
        };
        let startElement: IElementBaseModel;
        let endElement: IElementBaseModel;
        let currentFrame;
        if (connectorModel.startUIModel && (startElement = connectorModel.startUIModel)) {
            currentFrame =  {
                ...startElement.getFrame?.().getFrameInfo(!!startElement.operationFrame),
                ...toUpdateModelFrameMap?.get(connectorModel.startUIModel.key),
            };
            const startFrame = connectorModel.startUIModel.getFrame?.().frameOnSlide(currentFrame, isOperateIng);
            const startRect = {
                left: Math.round(startFrame.left),
                top: Math.round(startFrame.top),
                right: Math.round(startFrame.width + startFrame.left),
                bottom: Math.round(startFrame.height + startFrame.top),
            };
            start.rect = startRect;
            if (has(startFrame, 'rotate')) {
                start.rotate = startFrame.rotate;
            }
        }
        if (connectorModel.endUIModel && (endElement = connectorModel.endUIModel)) {
            currentFrame =  {
                ...endElement.getFrame?.().getFrameInfo(!!endElement.operationFrame),
                ...toUpdateModelFrameMap?.get(connectorModel.endUIModel.key),
            };
            const endFrame = connectorModel.endUIModel.getFrame?.().frameOnSlide(currentFrame, isOperateIng);
            const endRect = {
                left: Math.round(endFrame.left),
                top: Math.round(endFrame.top),
                right: Math.round(endFrame.width + endFrame.left),
                bottom: Math.round(endFrame.height + endFrame.top),
            };

            end.rect = endRect;
            if (has(endFrame, 'rotate')) {
                end.rotate = endFrame.rotate;
            }
        }
        return {
            start,
            end,
        };
    }

    /**
     * 获取连接线需要更新的style信息
     */
    private getStyleProps = (
        startP: IPointerInfo, endP: IPointerInfo, lineType: ShapeType,
        toUpdateModelFrameMap?: IMultiModelFrameMap,
    ): IUpdateStyle => {
        let { controlValues, bgLine } = this.props.style;
        if (this.validateLineType(lineType)) {
            if (this.connectedNum() !== 0) {
                return this.getConnectedValidateLineTypeStyle(startP, endP, lineType, toUpdateModelFrameMap);
            }
            bgLine = lineType.startsWith('bentConnector') ? Lines.LineType.BENTCONNECTOR3 : Lines.LineType.CURVEDCONNECTOR3;
            controlValues = [0.5];
        }
        return {
            frame: this.getOffsetFrame(
                this.connectorCaculater.updatePointInGroup(startP),
                this.connectorCaculater.updatePointInGroup(endP),
            ),
            controlValues,
            bgLine,
        };
    };

    /**
     * 获取不为肘形/曲线连接线 或者 两端未连接元素的待更新的frame
     */
    private getOffsetFrame = (startP: IPointerInfo, endP: IPointerInfo): IFrame => {
        let flipH = 0;
        let flipV = 0;
        if (startP.x > endP.x) {
            const t = startP.x;
            startP.x = endP.x;
            endP.x = t;
            flipH = 1;
        }
        if (startP.y > endP.y) {
            const t = startP.y;
            startP.y = endP.y;
            endP.y = t;
            flipV = 1;
        }
        return {
            left: startP.x,
            top: startP.y,
            width: endP.x - startP.x,
            height: endP.y - startP.y,
            flipH,
            flipV,
            rotate: 0,
        };
    };
}

export default ConnectorModel;
